import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PushQuoteService } from '@zero/shared/ui-push';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { AssetClassOverviewInfrastructure } from '../infrastructure/asset-class-overview.infrastructure';
import { assetClassOverviewActions, assetClassOverviewApiActions } from './asset-class-overview.actions';

@Injectable({ providedIn: 'root' })
export class AssetClassOverviewEffects {
  readonly #actions$ = inject(Actions);
  readonly #infra = inject(AssetClassOverviewInfrastructure);
  readonly #storeErrorHandler = inject(StoreErrorHandler);
  readonly #pushQuoteService = inject(PushQuoteService);

  public loadEtfsByIndices$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(assetClassOverviewActions.loadEtfsByIndices),
      switchMap(() =>
        this.#infra.loadEtfsByIndices().pipe(
          map((response) => assetClassOverviewApiActions.loadEtfsByIndicesSuccess({ response })),
          catchError((error) => of(assetClassOverviewApiActions.loadEtfsByIndicesFailure({ error }))),
        ),
      ),
    );
  });

  public loadMostPopularEtfs$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(assetClassOverviewActions.loadMostPopularEtfs),
      switchMap(() =>
        this.#infra.loadMostPopularEtfs().pipe(
          map((response) => assetClassOverviewApiActions.loadMostPopularEtfsSuccess({ response })),
          catchError((error) => of(assetClassOverviewApiActions.loadMostPopularEtfsFailure({ error }))),
        ),
      ),
    );
  });

  public loadMostPopularStocks$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(assetClassOverviewActions.loadMostPopularStocks),
      switchMap(() =>
        this.#infra.loadMostPopularStocks().pipe(
          map((response) => assetClassOverviewApiActions.loadMostPopularStocksSuccess({ response })),
          catchError((error) => of(assetClassOverviewApiActions.loadMostPopularStocksFailure({ error }))),
        ),
      ),
    );
  });

  public loadMostActiveAssets$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(assetClassOverviewActions.loadMostActiveAssets),
      switchMap(({ instrumentType }) =>
        this.#infra.loadMostActiveAssets(instrumentType).pipe(
          map((response) => assetClassOverviewApiActions.loadMostActiveAssetsSuccess({ response })),
          catchError((error) => of(assetClassOverviewApiActions.loadMostActiveAssetsFailure({ error }))),
        ),
      ),
    );
  });

  public loadCallMoneyAlternatives$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(assetClassOverviewActions.loadCallMoneyAlternatives),
      switchMap(() =>
        this.#infra.loadCallMoneyAlternatives().pipe(
          map((response) => assetClassOverviewApiActions.loadCallMoneyAlternativesSuccess({ response })),
          catchError((error) => of(assetClassOverviewApiActions.loadCallMoneyAlternativesFailure({ error }))),
        ),
      ),
    );
  });

  public subscribeToMostPopularEtfsQuotes$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(assetClassOverviewApiActions.loadMostPopularEtfsSuccess),
      switchMap(({ response }) =>
        this.#pushQuoteService
          .subscribeMultiBuffered([...response.position, ...response.watchlist, ...response.savingsPlan].map((etf) => etf.isin))
          .pipe(
            filter((quotes) => !!quotes && !!Object.keys(quotes).length),
            map((quotes) => assetClassOverviewActions.updateMostPopularEtfsQuotes({ quotes })),
          ),
      ),
    );
  });

  public subscribeToMostPopularStocksQuotes$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(assetClassOverviewApiActions.loadMostPopularStocksSuccess),
      switchMap(({ response }) =>
        this.#pushQuoteService
          .subscribeMultiBuffered([...response.position, ...response.watchlist, ...response.savingsPlan].map((etf) => etf.isin))
          .pipe(
            filter((quotes) => !!quotes && !!Object.keys(quotes).length),
            map((quotes) => assetClassOverviewActions.updateMostPopularStocksQuotes({ quotes })),
          ),
      ),
    );
  });

  public subscribeToMostActiveAssetsQuotes$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(assetClassOverviewApiActions.loadMostActiveAssetsSuccess),
      switchMap(({ response }) =>
        this.#pushQuoteService
          .subscribeMultiBuffered(
            [...response.instrumentsByBuyRank, ...response.instrumentsBySellRank, ...response.instrumentsByRank].map((etf) => etf.isin),
          )
          .pipe(
            filter((quotes) => !!quotes && !!Object.keys(quotes).length),
            map((quotes) => assetClassOverviewActions.updateMostActiveAssetsQuotes({ quotes })),
          ),
      ),
    );
  });

  public loadWinnerLoser$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(assetClassOverviewActions.loadWinnerLoser),
      switchMap(({ instrumentType }) =>
        this.#infra.loadWinnerLoser(instrumentType).pipe(
          map((response) => assetClassOverviewApiActions.loadWinnerLoserSuccess({ response })),
          catchError((error) => of(assetClassOverviewApiActions.loadWinnerLoserFailure({ error }))),
        ),
      ),
    );
  });

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(
          assetClassOverviewApiActions.loadEtfsByIndicesFailure,
          assetClassOverviewApiActions.loadMostPopularEtfsFailure,
          assetClassOverviewApiActions.loadCallMoneyAlternativesFailure,
          assetClassOverviewApiActions.loadMostActiveAssetsFailure,
          assetClassOverviewApiActions.loadWinnerLoserFailure,
          assetClassOverviewApiActions.loadMostPopularStocksFailure,
        ),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
